import { DateTime } from 'luxon';
import type { TrialBannerWarmupStats } from '../types';

/** Last calendar day (1-based from subscription startAt) of the 30-day extension eligibility window. */
const EXTENSION_ELIGIBILITY_LAST_DAY = 30;
/** First day of the 23-day extension window (days 8–30 after the initial 7-day trial). */
const EXTENSION_WINDOW_FIRST_DAY = 8;

function toFiniteNumber(value: unknown): number | null {
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}

/**
 * Normalizes GET /user/trial-banner payload into UI stats.
 * Supports camelCase or snake_case keys depending on backend.
 */
export function parseTrialBannerPayload(
  payload: unknown,
): TrialBannerWarmupStats | null {
  if (payload == null) return null;

  const raw =
    typeof payload === 'object' && payload !== null
      ? (payload as Record<string, unknown>)
      : null;

  if (!raw) return null;

  const nested =
    (raw.trialBanner as Record<string, unknown> | undefined) ??
    (raw.stats as Record<string, unknown> | undefined);

  const source = nested ?? raw;

  const warmupMailsSent = toFiniteNumber(
    source.warmupMailsSent ??
      source.warmup_mails_sent ??
      source.totalWarmupMailsSent ??
      source.total_warmup_mails_sent ??
      source.totalSent ??
      source.total_sent,
  );
  const rescuedFromSpam = toFiniteNumber(
    source.rescuedFromSpam ??
      source.rescued_from_spam ??
      source.spamRescued ??
      source.spam_rescued ??
      source.totalSpam ??
      source.total_spam,
  );
  const daysBuildingScore = toFiniteNumber(
    source.daysBuildingScore ??
      source.days_building_score ??
      source.daysBuilding ??
      source.days_building ??
      source.currentDay ??
      source.current_day,
  );

  const activeEmailAccounts = toFiniteNumber(
    source.activeEmailAccounts ?? source.active_email_accounts,
  );
  const pausedEmailAccounts = toFiniteNumber(
    source.pausedEmailAccounts ?? source.paused_email_accounts,
  );

  if (
    warmupMailsSent == null &&
    rescuedFromSpam == null &&
    daysBuildingScore == null &&
    activeEmailAccounts == null &&
    pausedEmailAccounts == null
  ) {
    return null;
  }

  return {
    warmupMailsSent: warmupMailsSent ?? 0,
    rescuedFromSpam: rescuedFromSpam ?? 0,
    daysBuildingScore: daysBuildingScore ?? 0,
    ...(activeEmailAccounts != null ? { activeEmailAccounts } : {}),
    ...(pausedEmailAccounts != null ? { pausedEmailAccounts } : {}),
  };
}

/**
 * App-wide trial countdown banner rules (from startAt / nextBillingAt / isTrialExtended):
 * Applies to **trial** or **free** plan (same calendar-day window from startAt).
 * - Original 7-day trial: days 1–5 hidden; days 6–7 visible (last ~2 days before nextBillingAt).
 * - Days 8–30 from startAt: visible if the user has not extended yet (the 23-day extension window after the first 7 days).
 * - If isTrialExtended: only the last ~2 days before nextBillingAt; after nextBillingAt, hidden.
 * - Days 8–30: still show after original trial end when plan is free and nextBillingAt is in the past
 *   (one-time extend window is based on startAt, not on that stale billing timestamp).
 */
export function shouldShowTrialCountdownBanner(args: {
  isTrial: boolean;
  /** Free users who left trial (or on free) use the same day 6–7 / 8–30 window when not extended. */
  isFreePlan?: boolean;
  startAt: string | null | undefined;
  nextBillingAt: string | null | undefined;
  isTrialExtended: boolean;
}): boolean {
  const {
    isTrial,
    isFreePlan = false,
    startAt,
    nextBillingAt,
    isTrialExtended,
  } = args;
  if (!startAt) return false;
  if (!isTrial && !isFreePlan) return false;

  const start = DateTime.fromISO(startAt, { zone: 'Etc/UTC' });
  if (!start.isValid) return false;

  let end: DateTime | null = null;
  if (nextBillingAt != null && String(nextBillingAt).trim() !== '') {
    const parsed = DateTime.fromISO(nextBillingAt, { zone: 'Etc/UTC' });
    if (!parsed.isValid) return false;
    end = parsed;
  }

  const now = DateTime.now().toUTC();
  const dayIndex =
    Math.floor(now.startOf('day').diff(start.startOf('day'), 'days').days) + 1;

  // One-time extend window (days 8–30 from startAt): only needs startAt. Free users often have
  // nextBillingAt cleared or missing after downgrade, but the 30-day window still applies.
  if (
    !isTrialExtended &&
    dayIndex >= EXTENSION_WINDOW_FIRST_DAY &&
    dayIndex <= EXTENSION_ELIGIBILITY_LAST_DAY
  ) {
    return true;
  }

  // First-week / extended-trial countdown rules need a billing end timestamp.
  if (!end) {
    return false;
  }

  if (now >= end) return false;

  const daysRemaining = end.diff(now, ['days']).days ?? 0;
  const ceilDaysRemaining = Math.ceil(daysRemaining);

  if (isTrialExtended) {
    return ceilDaysRemaining <= 2 && ceilDaysRemaining > 0;
  }

  if (dayIndex <= 5) return false;
  if (dayIndex <= 7) return true;

  return false;
}

/**
 * End of the 30-day eligibility window (day 30), for countdown when `nextBillingAt` is missing on free plan.
 */
export function getSyntheticExtensionWindowEndIso(
  startAt: string,
): string | null {
  const start = DateTime.fromISO(startAt, { zone: 'Etc/UTC' });
  if (!start.isValid) return null;
  return start.plus({ days: 29 }).endOf('day').toISO();
}

const DOWNGRADE_CRON_HOURS = [5, 11, 17, 23];
const DOWNGRADE_CRON_MINUTE = 31;
const DOWNGRADE_CRON_ZONE = 'Asia/Kolkata';

/**
 * Returns the next cron slot (05:31, 11:31, 17:31, 23:31 IST) after the given ISO date string.
 * Used to show users the actual time their plan will be downgraded, not just endAt.
 */
export function getNextCronAlignedIso(isoDate: string): string {
  const dt = DateTime.fromISO(isoDate, { zone: DOWNGRADE_CRON_ZONE });
  if (!dt.isValid) return isoDate;

  for (const hour of DOWNGRADE_CRON_HOURS) {
    const candidate = dt
      .startOf('day')
      .set({ hour, minute: DOWNGRADE_CRON_MINUTE, second: 0, millisecond: 0 });
    if (candidate > dt) return candidate.toISO();
  }

  return dt
    .plus({ days: 1 })
    .startOf('day')
    .set({
      hour: DOWNGRADE_CRON_HOURS[0],
      minute: DOWNGRADE_CRON_MINUTE,
      second: 0,
      millisecond: 0,
    })
    .toISO();
}

/** Same calendar-day rule as days 8–30 in `shouldShowTrialCountdownBanner` (for Extend CTA when API omits eligibility flag). */
export function isInOneTimeTrialExtensionWindow(args: {
  startAt: string | null | undefined;
  isTrialExtended: boolean;
}): boolean {
  const { startAt, isTrialExtended } = args;
  if (!startAt || isTrialExtended) return false;
  const start = DateTime.fromISO(startAt, { zone: 'Etc/UTC' });
  if (!start.isValid) return false;
  const now = DateTime.now().toUTC();
  const dayIndex =
    Math.floor(now.startOf('day').diff(start.startOf('day'), 'days').days) + 1;
  return (
    dayIndex >= EXTENSION_WINDOW_FIRST_DAY &&
    dayIndex <= EXTENSION_ELIGIBILITY_LAST_DAY
  );
}
